import type { DesignElement } from "./design";
import type { CanvasView } from "./viewport";

// iPadOS can request desktop sites and identify as a Mac. Touch support alone
// would also select Windows laptops, whose existing editor gestures stay intact.
export function usesNativeCanvasNavigation(device: Pick<Navigator, "userAgent" | "platform" | "maxTouchPoints"> | undefined = typeof navigator === "undefined" ? undefined : navigator) {
  return !!device && (/iPad|iPhone|iPod/.test(device.userAgent) ||
    (device.platform === "MacIntel" && device.maxTouchPoints > 1));
}

export type CanvasBounds = { left: number; top: number; right: number; bottom: number };
export function nativeCanvasBounds(elements: DesignElement[]): CanvasBounds {
  const bounds = { left: 0, top: 0, right: 0, bottom: 0 };
  for (const e of elements) {
    if (e.parentId || e.hidden) continue;
    const angle = e.rotation * Math.PI / 180;
    const halfWidth = (Math.abs(Math.cos(angle)) * e.width + Math.abs(Math.sin(angle)) * e.height) / 2;
    const halfHeight = (Math.abs(Math.sin(angle)) * e.width + Math.abs(Math.cos(angle)) * e.height) / 2;
    const x = e.x + e.width / 2, y = e.y + e.height / 2;
    bounds.left = Math.min(bounds.left, x - halfWidth); bounds.right = Math.max(bounds.right, x + halfWidth);
    bounds.top = Math.min(bounds.top, y - halfHeight); bounds.bottom = Math.max(bounds.bottom, y + halfHeight);
  }
  return bounds;
}

/** Leave room around the artwork and the requested view, including negative coordinates. */
export function nativeCanvasLayout(view: CanvasView, bounds: CanvasBounds, size: { width: number; height: number }) {
  const padding = Math.max(512, size.width, size.height) * 2;
  const origin = { x: Math.max(padding - bounds.left * view.zoom, view.pan.x + padding),
    y: Math.max(padding - bounds.top * view.zoom, view.pan.y + padding) };
  const scroll = { x: origin.x - view.pan.x, y: origin.y - view.pan.y };
  return { origin, scroll,
    width: Math.ceil(Math.max(origin.x + bounds.right * view.zoom + padding, scroll.x + size.width + padding)),
    height: Math.ceil(Math.max(origin.y + bounds.bottom * view.zoom + padding, scroll.y + size.height + padding)) };
}
